import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Contract, Person, LegalEntity, LandPlot, InstallmentPayment } from '../types/Contract';
import { format } from 'date-fns';
import { PersonSelector } from './PersonSelector';
import { useContracts } from '../hooks/useContracts';
import './ContractForm.css';

interface ContractFormProps {
  onSubmit: (contract: Contract) => void;
  initialData?: Contract;
  disabled?: boolean;
}

interface FormData {
  type: 'main' | 'preliminary' | 'installment';
  contractNumber?: string;
  dealCity: string;
  // Поля для рассрочки
  initialPayment?: number | string;
  hasEncumbrance?: boolean;
  latePaymentPenalty?: string;
  // Поля для предварительного договора
  mainContractDeadline?: string;
  deposit?: number | string;
  depositType?: 'deposit' | 'advance';

  // Тип продавца и покупателя
  sellerType: 'individual' | 'legal_entity';
  buyerType: 'individual' | 'legal_entity';

  // Данные продавца (Физлицо)
  sellerLastName?: string;
  sellerFirstName?: string;
  sellerMiddleName?: string;
  sellerGender?: 'male' | 'female';
  sellerPassportSeries?: string;
  sellerPassportNumber?: string;
  sellerPassportIssueDate?: string;
  sellerPassportIssuer?: string;
  sellerPassportCode?: string;
  sellerBirthDate?: string;
  sellerAddress?: string;
  sellerPhone?: string;
  sellerCardNumber?: string;
  sellerIndividualBankName?: string;
  sellerIndividualBik?: string;
  sellerIndividualCorrespondentAccount?: string;
  sellerIndividualCheckingAccount?: string;
  // Поля для доверенности продавца
  sellerIsActingByPowerOfAttorney?: boolean;
  sellerPowerOfAttorneyNumber?: string;
  sellerPowerOfAttorneyDate?: string;
  sellerPowerOfAttorneyIssuer?: string;
  sellerPowerOfAttorneyIssuerAddress?: string;
  // Данные доверителя продавца (настоящий собственник)
  sellerPrincipalLastName?: string;
  sellerPrincipalFirstName?: string;
  sellerPrincipalMiddleName?: string;
  sellerPrincipalGender?: 'male' | 'female';
  sellerPrincipalPassportSeries?: string;
  sellerPrincipalPassportNumber?: string;
  sellerPrincipalPassportIssueDate?: string;
  sellerPrincipalPassportIssuer?: string;
  sellerPrincipalPassportCode?: string;
  sellerPrincipalBirthDate?: string;
  sellerPrincipalAddress?: string;
  sellerPrincipalPhone?: string;

  // Данные продавца (Юрлицо)
  sellerLegalName?: string;
  sellerInn?: string;
  sellerKpp?: string;
  sellerOgrn?: string;
  sellerLegalAddress?: string;
  sellerMailingAddress?: string;
  sellerBankName?: string;
  sellerBik?: string;
  sellerCorrespondentAccount?: string;
  sellerCheckingAccount?: string;
  sellerDirectorName?: string;
  sellerDirectorPosition?: string;
  sellerActingBasis?: string;
  sellerLegalPhone?: string;
  sellerLegalEmail?: string;

  // Данные покупателя (Физлицо)
  buyerLastName?: string;
  buyerFirstName?: string;
  buyerMiddleName?: string;
  buyerGender?: 'male' | 'female';
  buyerPassportSeries?: string;
  buyerPassportNumber?: string;
  buyerPassportIssueDate?: string;
  buyerPassportIssuer?: string;
  buyerPassportCode?: string;
  buyerBirthDate?: string;
  buyerAddress?: string;
  buyerPhone?: string;
  buyerCardNumber?: string;
  buyerIndividualBankName?: string;
  buyerIndividualBik?: string;
  buyerIndividualCorrespondentAccount?: string;
  buyerIndividualCheckingAccount?: string;

  // Данные покупателя (Юрлицо)
  buyerLegalName?: string;
  buyerInn?: string;
  buyerKpp?: string;
  buyerOgrn?: string;
  buyerLegalAddress?: string;
  buyerMailingAddress?: string;
  buyerBankName?: string;
  buyerBik?: string;
  buyerCorrespondentAccount?: string;
  buyerCheckingAccount?: string;
  buyerDirectorName?: string;
  buyerDirectorPosition?: string;
  buyerActingBasis?: string;
  buyerLegalPhone?: string;
  buyerLegalEmail?: string;

  cadastralNumber: string;
  area: number;
  category: string;
  purpose: string;
  landAddress: string;
  rightType: string;
  rightRegistrationNumber: string;
  rightRegistrationDate: string;
  registrationBasis: string;
  dealDate: string;
  amount: number | string;
  additionalConditions?: string;
  // Способ оплаты
  paymentMethod?: 'cash' | 'bank_transfer' | 'card_transfer' | 'mixed';
  cashAmount?: number | string;
  transferAmount?: number | string;
}

export const ContractForm: React.FC<ContractFormProps> = ({ onSubmit, initialData, disabled = false }) => {
  const { getUniqueSellers, getUniqueBuyers } = useContracts();
  const [selectedSeller, setSelectedSeller] = useState<Person | LegalEntity | null>(null);
  const [selectedBuyer, setSelectedBuyer] = useState<Person | LegalEntity | null>(null);
  const [sellerActsByPowerOfAttorney, setSellerActsByPowerOfAttorney] = useState<boolean>(
    (initialData?.seller as Person)?.isActingByPowerOfAttorney || false
  );
  const [installmentPayments, setInstallmentPayments] = useState<InstallmentPayment[]>(
    initialData?.installmentPayments || []
  );

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<FormData>({
    defaultValues: initialData ? {
      type: initialData.type,
      contractNumber: initialData.contractNumber,
      dealCity: initialData.dealCity,
      initialPayment: initialData.initialPayment,
      hasEncumbrance: initialData.hasEncumbrance,
      latePaymentPenalty: initialData.latePaymentPenalty,
      mainContractDeadline: initialData.mainContractDeadline,
      deposit: initialData.deposit,
      depositType: initialData.depositType,

      // Типы сторон
      sellerType: (initialData.seller as any).type === 'legal_entity' ? 'legal_entity' : 'individual',
      buyerType: (initialData.buyer as any).type === 'legal_entity' ? 'legal_entity' : 'individual',

      // Данные продавца (Физлицо)
      sellerLastName: (initialData.seller as Person).lastName,
      sellerFirstName: (initialData.seller as Person).firstName,
      sellerMiddleName: (initialData.seller as Person).middleName,
      sellerGender: (initialData.seller as Person).gender,
      sellerPassportSeries: (initialData.seller as Person).passportSeries,
      sellerPassportNumber: (initialData.seller as Person).passportNumber,
      sellerPassportIssueDate: (initialData.seller as Person).passportIssueDate,
      sellerPassportIssuer: (initialData.seller as Person).passportIssuer,
      sellerPassportCode: (initialData.seller as Person).passportCode,
      sellerBirthDate: (initialData.seller as Person).birthDate,
      sellerAddress: (initialData.seller as Person).address,
      sellerPhone: (initialData.seller as Person).phone,
      sellerCardNumber: (initialData.seller as Person).cardNumber,
      sellerIndividualBankName: (initialData.seller as Person).bankName,
      sellerIndividualBik: (initialData.seller as Person).bik,
      sellerIndividualCorrespondentAccount: (initialData.seller as Person).correspondentAccount,
      sellerIndividualCheckingAccount: (initialData.seller as Person).checkingAccount,
      // Поля доверенности продавца
      sellerIsActingByPowerOfAttorney: (initialData.seller as Person).isActingByPowerOfAttorney,
      sellerPowerOfAttorneyNumber: (initialData.seller as Person).powerOfAttorney?.number,
      sellerPowerOfAttorneyDate: (initialData.seller as Person).powerOfAttorney?.date,
      sellerPowerOfAttorneyIssuer: (initialData.seller as Person).powerOfAttorney?.issuer,
      sellerPowerOfAttorneyIssuerAddress: (initialData.seller as Person).powerOfAttorney?.issuerAddress,
      // Данные доверителя продавца
      sellerPrincipalLastName: (initialData.seller as Person).principal?.lastName,
      sellerPrincipalFirstName: (initialData.seller as Person).principal?.firstName,
      sellerPrincipalMiddleName: (initialData.seller as Person).principal?.middleName,
      sellerPrincipalGender: (initialData.seller as Person).principal?.gender,
      sellerPrincipalPassportSeries: (initialData.seller as Person).principal?.passportSeries,
      sellerPrincipalPassportNumber: (initialData.seller as Person).principal?.passportNumber,
      sellerPrincipalPassportIssueDate: (initialData.seller as Person).principal?.passportIssueDate,
      sellerPrincipalPassportIssuer: (initialData.seller as Person).principal?.passportIssuer,
      sellerPrincipalPassportCode: (initialData.seller as Person).principal?.passportCode,
      sellerPrincipalBirthDate: (initialData.seller as Person).principal?.birthDate,
      sellerPrincipalAddress: (initialData.seller as Person).principal?.address,
      sellerPrincipalPhone: (initialData.seller as Person).principal?.phone,

      // Данные продавца (Юрлицо)
      sellerLegalName: (initialData.seller as LegalEntity).name,
      sellerInn: (initialData.seller as LegalEntity).inn,
      sellerKpp: (initialData.seller as LegalEntity).kpp,
      sellerOgrn: (initialData.seller as LegalEntity).ogrn,
      sellerLegalAddress: (initialData.seller as LegalEntity).legalAddress,
      sellerMailingAddress: (initialData.seller as LegalEntity).mailingAddress,
      sellerBankName: (initialData.seller as LegalEntity).bankName,
      sellerBik: (initialData.seller as LegalEntity).bik,
      sellerCorrespondentAccount: (initialData.seller as LegalEntity).correspondentAccount,
      sellerCheckingAccount: (initialData.seller as LegalEntity).checkingAccount,
      sellerDirectorName: (initialData.seller as LegalEntity).directorName,
      sellerDirectorPosition: (initialData.seller as LegalEntity).directorPosition,
      sellerActingBasis: (initialData.seller as LegalEntity).actingBasis,
      sellerLegalPhone: (initialData.seller as LegalEntity).phone,
      sellerLegalEmail: (initialData.seller as LegalEntity).email,

      // Данные покупателя (Физлицо)
      buyerLastName: (initialData.buyer as Person).lastName,
      buyerFirstName: (initialData.buyer as Person).firstName,
      buyerMiddleName: (initialData.buyer as Person).middleName,
      buyerGender: (initialData.buyer as Person).gender,
      buyerPassportSeries: (initialData.buyer as Person).passportSeries,
      buyerPassportNumber: (initialData.buyer as Person).passportNumber,
      buyerPassportIssueDate: (initialData.buyer as Person).passportIssueDate,
      buyerPassportIssuer: (initialData.buyer as Person).passportIssuer,
      buyerPassportCode: (initialData.buyer as Person).passportCode,
      buyerBirthDate: (initialData.buyer as Person).birthDate,
      buyerAddress: (initialData.buyer as Person).address,
      buyerPhone: (initialData.buyer as Person).phone,
      buyerCardNumber: (initialData.buyer as Person).cardNumber,
      buyerIndividualBankName: (initialData.buyer as Person).bankName,
      buyerIndividualBik: (initialData.buyer as Person).bik,
      buyerIndividualCorrespondentAccount: (initialData.buyer as Person).correspondentAccount,
      buyerIndividualCheckingAccount: (initialData.buyer as Person).checkingAccount,

      // Данные покупателя (Юрлицо)
      buyerLegalName: (initialData.buyer as LegalEntity).name,
      buyerInn: (initialData.buyer as LegalEntity).inn,
      buyerKpp: (initialData.buyer as LegalEntity).kpp,
      buyerOgrn: (initialData.buyer as LegalEntity).ogrn,
      buyerLegalAddress: (initialData.buyer as LegalEntity).legalAddress,
      buyerMailingAddress: (initialData.buyer as LegalEntity).mailingAddress,
      buyerBankName: (initialData.buyer as LegalEntity).bankName,
      buyerBik: (initialData.buyer as LegalEntity).bik,
      buyerCorrespondentAccount: (initialData.buyer as LegalEntity).correspondentAccount,
      buyerCheckingAccount: (initialData.buyer as LegalEntity).checkingAccount,
      buyerDirectorName: (initialData.buyer as LegalEntity).directorName,
      buyerDirectorPosition: (initialData.buyer as LegalEntity).directorPosition,
      buyerActingBasis: (initialData.buyer as LegalEntity).actingBasis,
      buyerLegalPhone: (initialData.buyer as LegalEntity).phone,
      buyerLegalEmail: (initialData.buyer as LegalEntity).email,

      cadastralNumber: initialData.landPlot.cadastralNumber,
      area: initialData.landPlot.area,
      category: initialData.landPlot.category,
      purpose: initialData.landPlot.purpose,
      landAddress: initialData.landPlot.address,
      rightType: initialData.landPlot.rightType,
      rightRegistrationNumber: initialData.landPlot.rightRegistrationNumber,
      rightRegistrationDate: initialData.landPlot.rightRegistrationDate,
      registrationBasis: initialData.landPlot.registrationBasis,
      dealDate: initialData.dealDate,
      amount: initialData.amount,
      additionalConditions: initialData.additionalConditions,
      paymentMethod: initialData.paymentMethod || 'bank_transfer',
      cashAmount: initialData.cashAmount,
      transferAmount: initialData.transferAmount
    } : {
      type: 'main',
      sellerType: 'individual',
      buyerType: 'individual',
      paymentMethod: 'bank_transfer'
    }
  });

  const contractType = watch('type');

  // Очистка графика платежей при переключении типа договора
  React.useEffect(() => {
    if (contractType !== 'installment' && installmentPayments.length > 0) {
      setInstallmentPayments([]);
    }
  }, [contractType, installmentPayments.length]);

  // Обработка переключения доверенности
  const handlePowerOfAttorneyToggle = (isChecked: boolean) => {
    setSellerActsByPowerOfAttorney(isChecked);
    setValue('sellerIsActingByPowerOfAttorney', isChecked);

    if (isChecked) {
      // При включении доверенности сохраняем текущие данные продавца как данные представителя
      // (они уже в форме, ничего перемещать не нужно)
      // Очищаем поля доверителя для заполнения
      setValue('sellerPrincipalLastName', '');
      setValue('sellerPrincipalFirstName', '');
      setValue('sellerPrincipalMiddleName', '');
      setValue('sellerPrincipalGender', '' as 'male' | 'female');
      setValue('sellerPrincipalPassportSeries', '');
      setValue('sellerPrincipalPassportNumber', '');
      setValue('sellerPrincipalPassportIssueDate', '');
      setValue('sellerPrincipalPassportIssuer', '');
      setValue('sellerPrincipalPassportCode', '');
      setValue('sellerPrincipalBirthDate', '');
      setValue('sellerPrincipalAddress', '');
      setValue('sellerPrincipalPhone', '');
      setValue('sellerPowerOfAttorneyNumber', '');
      setValue('sellerPowerOfAttorneyDate', '');
      setValue('sellerPowerOfAttorneyIssuer', '');
      setValue('sellerPowerOfAttorneyIssuerAddress', '');
    } else {
      // При выключении доверенности очищаем все поля доверенности
      setValue('sellerPrincipalLastName', '');
      setValue('sellerPrincipalFirstName', '');
      setValue('sellerPrincipalMiddleName', '');
      setValue('sellerPrincipalGender', '' as 'male' | 'female');
      setValue('sellerPrincipalPassportSeries', '');
      setValue('sellerPrincipalPassportNumber', '');
      setValue('sellerPrincipalPassportIssueDate', '');
      setValue('sellerPrincipalPassportIssuer', '');
      setValue('sellerPrincipalPassportCode', '');
      setValue('sellerPrincipalBirthDate', '');
      setValue('sellerPrincipalAddress', '');
      setValue('sellerPrincipalPhone', '');
      setValue('sellerPowerOfAttorneyNumber', '');
      setValue('sellerPowerOfAttorneyDate', '');
      setValue('sellerPowerOfAttorneyIssuer', '');
      setValue('sellerPowerOfAttorneyIssuerAddress', '');
    }
  };

  // Обработка выбора продавца
  const handleSellerSelect = (seller: Person | LegalEntity | null) => {
    setSelectedSeller(seller);
    if (seller) {
      if ((seller as any).type === 'legal_entity') {
        const legalSeller = seller as LegalEntity;
        setValue('sellerType', 'legal_entity');
        setValue('sellerLegalName', legalSeller.name);
        setValue('sellerInn', legalSeller.inn);
        setValue('sellerKpp', legalSeller.kpp);
        setValue('sellerOgrn', legalSeller.ogrn);
        setValue('sellerLegalAddress', legalSeller.legalAddress);
        setValue('sellerMailingAddress', legalSeller.mailingAddress);
        setValue('sellerBankName', legalSeller.bankName);
        setValue('sellerBik', legalSeller.bik);
        setValue('sellerCorrespondentAccount', legalSeller.correspondentAccount);
        setValue('sellerCheckingAccount', legalSeller.checkingAccount);
        setValue('sellerDirectorName', legalSeller.directorName);
        setValue('sellerDirectorPosition', legalSeller.directorPosition);
        setValue('sellerActingBasis', legalSeller.actingBasis);
        setValue('sellerLegalPhone', legalSeller.phone);
        setValue('sellerLegalEmail', legalSeller.email);
      } else {
        const personSeller = seller as Person;
        setValue('sellerType', 'individual');
        setValue('sellerLastName', personSeller.lastName);
        setValue('sellerFirstName', personSeller.firstName);
        setValue('sellerMiddleName', personSeller.middleName || '');
        setValue('sellerGender', personSeller.gender);
        setValue('sellerPassportSeries', personSeller.passportSeries);
        setValue('sellerPassportNumber', personSeller.passportNumber);
        setValue('sellerPassportIssueDate', personSeller.passportIssueDate);
        setValue('sellerPassportIssuer', personSeller.passportIssuer);
        setValue('sellerPassportCode', personSeller.passportCode);
        setValue('sellerBirthDate', personSeller.birthDate);
        setValue('sellerAddress', personSeller.address);
        setValue('sellerPhone', personSeller.phone || '');
        setValue('sellerCardNumber', personSeller.cardNumber || '');
        setValue('sellerIndividualBankName', personSeller.bankName || '');
        setValue('sellerIndividualBik', personSeller.bik || '');
        setValue('sellerIndividualCorrespondentAccount', personSeller.correspondentAccount || '');
        setValue('sellerIndividualCheckingAccount', personSeller.checkingAccount || '');
      }
    }
  };

  // Обработка выбора покупателя
  const handleBuyerSelect = (buyer: Person | LegalEntity | null) => {
    setSelectedBuyer(buyer);
    if (buyer) {
      if ((buyer as any).type === 'legal_entity') {
        const legalBuyer = buyer as LegalEntity;
        setValue('buyerType', 'legal_entity');
        setValue('buyerLegalName', legalBuyer.name);
        setValue('buyerInn', legalBuyer.inn);
        setValue('buyerKpp', legalBuyer.kpp);
        setValue('buyerOgrn', legalBuyer.ogrn);
        setValue('buyerLegalAddress', legalBuyer.legalAddress);
        setValue('buyerMailingAddress', legalBuyer.mailingAddress);
        setValue('buyerBankName', legalBuyer.bankName);
        setValue('buyerBik', legalBuyer.bik);
        setValue('buyerCorrespondentAccount', legalBuyer.correspondentAccount);
        setValue('buyerCheckingAccount', legalBuyer.checkingAccount);
        setValue('buyerDirectorName', legalBuyer.directorName);
        setValue('buyerDirectorPosition', legalBuyer.directorPosition);
        setValue('buyerActingBasis', legalBuyer.actingBasis);
        setValue('buyerLegalPhone', legalBuyer.phone);
        setValue('buyerLegalEmail', legalBuyer.email);
      } else {
        const personBuyer = buyer as Person;
        setValue('buyerType', 'individual');
        setValue('buyerLastName', personBuyer.lastName);
        setValue('buyerFirstName', personBuyer.firstName);
        setValue('buyerMiddleName', personBuyer.middleName || '');
        setValue('buyerGender', personBuyer.gender);
        setValue('buyerPassportSeries', personBuyer.passportSeries);
        setValue('buyerPassportNumber', personBuyer.passportNumber);
        setValue('buyerPassportIssueDate', personBuyer.passportIssueDate);
        setValue('buyerPassportIssuer', personBuyer.passportIssuer);
        setValue('buyerPassportCode', personBuyer.passportCode);
        setValue('buyerBirthDate', personBuyer.birthDate);
        setValue('buyerAddress', personBuyer.address);
        setValue('buyerPhone', personBuyer.phone || '');
        setValue('buyerCardNumber', personBuyer.cardNumber || '');
        setValue('buyerIndividualBankName', personBuyer.bankName || '');
        setValue('buyerIndividualBik', personBuyer.bik || '');
        setValue('buyerIndividualCorrespondentAccount', personBuyer.correspondentAccount || '');
        setValue('buyerIndividualCheckingAccount', personBuyer.checkingAccount || '');
      }
    }
  };

  const onFormSubmit = (data: FormData) => {
    // Валидация для договора с рассрочкой
    if (data.type === 'installment' && (!installmentPayments || installmentPayments.length === 0)) {
      alert('Для договора с рассрочкой необходимо добавить хотя бы один платеж в график платежей.');
      return;
    }

    if (data.type === 'installment' && installmentPayments.some(p => !p.amount || !p.dueDate)) {
      alert('Все платежи в графике должны иметь сумму и срок оплаты.');
      return;
    }

    let seller: Person | LegalEntity;

    if (data.sellerType === 'legal_entity') {
      seller = {
        type: 'legal_entity',
        name: data.sellerLegalName!,
        inn: data.sellerInn!,
        kpp: data.sellerKpp,
        ogrn: data.sellerOgrn!,
        legalAddress: data.sellerLegalAddress!,
        mailingAddress: data.sellerMailingAddress,
        bankName: data.sellerBankName!,
        bik: data.sellerBik!,
        correspondentAccount: data.sellerCorrespondentAccount!,
        checkingAccount: data.sellerCheckingAccount!,
        directorName: data.sellerDirectorName!,
        directorPosition: data.sellerDirectorPosition!,
        actingBasis: data.sellerActingBasis!,
        phone: data.sellerLegalPhone,
        email: data.sellerLegalEmail
      };
    } else {
      seller = {
        type: 'individual',
        lastName: data.sellerLastName!,
        firstName: data.sellerFirstName!,
        middleName: data.sellerMiddleName,
        gender: data.sellerGender!,
        passportSeries: data.sellerPassportSeries!,
        passportNumber: data.sellerPassportNumber!,
        passportIssueDate: data.sellerPassportIssueDate!,
        passportIssuer: data.sellerPassportIssuer!,
        passportCode: data.sellerPassportCode!,
        birthDate: data.sellerBirthDate!,
        address: data.sellerAddress!,
        phone: data.sellerPhone,
        cardNumber: data.sellerCardNumber,
        bankName: data.sellerIndividualBankName,
        bik: data.sellerIndividualBik,
        correspondentAccount: data.sellerIndividualCorrespondentAccount,
        checkingAccount: data.sellerIndividualCheckingAccount,
        // Данные доверенности
        isActingByPowerOfAttorney: data.sellerIsActingByPowerOfAttorney,
        powerOfAttorney: data.sellerIsActingByPowerOfAttorney ? {
          number: data.sellerPowerOfAttorneyNumber!,
          date: data.sellerPowerOfAttorneyDate!,
          issuer: data.sellerPowerOfAttorneyIssuer!,
          issuerAddress: data.sellerPowerOfAttorneyIssuerAddress
        } : undefined,
        principal: data.sellerIsActingByPowerOfAttorney ? {
          lastName: data.sellerPrincipalLastName!,
          firstName: data.sellerPrincipalFirstName!,
          middleName: data.sellerPrincipalMiddleName,
          gender: data.sellerPrincipalGender!,
          passportSeries: data.sellerPrincipalPassportSeries!,
          passportNumber: data.sellerPrincipalPassportNumber!,
          passportIssueDate: data.sellerPrincipalPassportIssueDate!,
          passportIssuer: data.sellerPrincipalPassportIssuer!,
          passportCode: data.sellerPrincipalPassportCode!,
          birthDate: data.sellerPrincipalBirthDate!,
          address: data.sellerPrincipalAddress!,
          phone: data.sellerPrincipalPhone
        } : undefined
      };
    }

    let buyer: Person | LegalEntity;

    if (data.buyerType === 'legal_entity') {
      buyer = {
        type: 'legal_entity',
        name: data.buyerLegalName!,
        inn: data.buyerInn!,
        kpp: data.buyerKpp,
        ogrn: data.buyerOgrn!,
        legalAddress: data.buyerLegalAddress!,
        mailingAddress: data.buyerMailingAddress,
        bankName: data.buyerBankName!,
        bik: data.buyerBik!,
        correspondentAccount: data.buyerCorrespondentAccount!,
        checkingAccount: data.buyerCheckingAccount!,
        directorName: data.buyerDirectorName!,
        directorPosition: data.buyerDirectorPosition!,
        actingBasis: data.buyerActingBasis!,
        phone: data.buyerLegalPhone,
        email: data.buyerLegalEmail
      };
    } else {
      buyer = {
        type: 'individual',
        lastName: data.buyerLastName!,
        firstName: data.buyerFirstName!,
        middleName: data.buyerMiddleName,
        gender: data.buyerGender!,
        passportSeries: data.buyerPassportSeries!,
        passportNumber: data.buyerPassportNumber!,
        passportIssueDate: data.buyerPassportIssueDate!,
        passportIssuer: data.buyerPassportIssuer!,
        passportCode: data.buyerPassportCode!,
        birthDate: data.buyerBirthDate!,
        address: data.buyerAddress!,
        phone: data.buyerPhone,
        cardNumber: data.buyerCardNumber,
        bankName: data.buyerIndividualBankName,
        bik: data.buyerIndividualBik,
        correspondentAccount: data.buyerIndividualCorrespondentAccount,
        checkingAccount: data.buyerIndividualCheckingAccount
      };
    }

    const landPlot: LandPlot = {
      cadastralNumber: data.cadastralNumber,
      area: data.area,
      category: data.category,
      purpose: data.purpose,
      address: data.landAddress,
      rightType: data.rightType,
      rightRegistrationNumber: data.rightRegistrationNumber,
      rightRegistrationDate: data.rightRegistrationDate,
      registrationBasis: data.registrationBasis
    };

    const contract: Contract = {
      id: initialData?.id || Date.now().toString(),
      type: data.type,
      contractNumber: data.contractNumber,
      seller,
      buyer,
      landPlot,
      dealDate: data.dealDate,
      dealCity: data.dealCity,
      amount: data.amount,
      createdDate: initialData?.createdDate || format(new Date(), 'yyyy-MM-dd'),
      status: 'draft',
      additionalConditions: data.additionalConditions,
      paymentMethod: data.paymentMethod,
      cashAmount: data.cashAmount,
      transferAmount: data.transferAmount,
      // Поля для рассрочки
      ...(data.type === 'installment' && {
        initialPayment: data.initialPayment,
        hasEncumbrance: data.hasEncumbrance,
        latePaymentPenalty: data.latePaymentPenalty,
        installmentPayments: installmentPayments.length > 0 ? installmentPayments : undefined
      }),
      // Поля для предварительного договора
      ...(data.type === 'preliminary' && {
        mainContractDeadline: data.mainContractDeadline,
        deposit: data.deposit,
        depositType: data.depositType
      })
    };

    onSubmit(contract);
  };

  // Функции для управления графиком платежей
  const addInstallmentPayment = () => {
    setInstallmentPayments([...installmentPayments, { amount: '', dueDate: '', description: '' }]);
  };

  const removeInstallmentPayment = (index: number) => {
    setInstallmentPayments(installmentPayments.filter((_, i) => i !== index));
  };

  const updateInstallmentPayment = (index: number, field: keyof InstallmentPayment, value: string | number) => {
    const updated = [...installmentPayments];
    updated[index] = { ...updated[index], [field]: value };
    setInstallmentPayments(updated);
  };

  return (
    <div className="contract-form">
      <h2>{initialData ? 'Редактировать договор' : 'Создать новый договор'}</h2>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        {/* Тип договора */}
        <div className="form-section">
          <h3>Тип договора</h3>
          <div className="contract-type-selector">
            <button
              type="button"
              className={`contract-type-btn ${contractType === 'main' ? 'active' : ''}`}
              onClick={() => setValue('type', 'main')}
            >
              <span className="contract-type-icon">📄</span>
              <span className="contract-type-label">Основной договор</span>
              <span className="contract-type-desc">Полная оплата при подписании</span>
            </button>
            <button
              type="button"
              className={`contract-type-btn ${contractType === 'preliminary' ? 'active' : ''}`}
              onClick={() => setValue('type', 'preliminary')}
            >
              <span className="contract-type-icon">📋</span>
              <span className="contract-type-label">Предварительный договор</span>
              <span className="contract-type-desc">С последующим заключением основного</span>
            </button>
            <button
              type="button"
              className={`contract-type-btn ${contractType === 'installment' ? 'active' : ''}`}
              onClick={() => setValue('type', 'installment')}
            >
              <span className="contract-type-icon">💳</span>
              <span className="contract-type-label">Договор с рассрочкой</span>
              <span className="contract-type-desc">Оплата по графику платежей</span>
            </button>
          </div>
          <input
            type="hidden"
            {...register('type', { required: 'Выберите тип договора' })}
          />
          {errors.type && <span className="error">{errors.type.message}</span>}
        </div>

        {/* Номер договора */}
        <div className="form-section">
          <h3>Номер договора</h3>
          <div className="form-group">
            <label>Номер договора (необязательно)</label>
            <input
              {...register('contractNumber')}
              placeholder="Например: 123/2024 или ДКП-001"
            />
            <div className="field-description">
              Укажите номер договора, если он нужен. Если оставить пустым, номер не будет отображаться в заголовке.
            </div>
          </div>
        </div>

        {/* Данные продавца */}
        <div className="form-section">
          <h3>Данные продавца</h3>

          <div className="form-group">
            <label>Тип продавца</label>
            <div className="contract-type-selector" style={{ marginBottom: '20px' }}>
              <button
                type="button"
                className={`contract-type-btn ${watch('sellerType') === 'individual' ? 'active' : ''}`}
                onClick={() => setValue('sellerType', 'individual')}
                style={{ flex: 1, padding: '10px' }}
              >
                <span className="contract-type-label">Физическое лицо</span>
              </button>
              <button
                type="button"
                className={`contract-type-btn ${watch('sellerType') === 'legal_entity' ? 'active' : ''}`}
                onClick={() => setValue('sellerType', 'legal_entity')}
                style={{ flex: 1, padding: '10px' }}
              >
                <span className="contract-type-label">Юридическое лицо</span>
              </button>
            </div>
            <input type="hidden" {...register('sellerType')} />
          </div>

          <div className="form-group">
            <label>Выбрать продавца из существующих договоров</label>
            <PersonSelector
              type="seller"
              persons={getUniqueSellers()}
              selectedPerson={selectedSeller}
              onPersonSelect={handleSellerSelect}
              placeholder="Поиск продавца..."
            />
          </div>

          {watch('sellerType') === 'individual' ? (
            <>
              {/* Переключатель доверенности */}
              <div className="power-of-attorney-toggle">
                <input
                  type="checkbox"
                  id="power-of-attorney-checkbox"
                  {...register('sellerIsActingByPowerOfAttorney')}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    handlePowerOfAttorneyToggle(isChecked);
                  }}
                  checked={sellerActsByPowerOfAttorney}
                />
                <label className="toggle-switch" htmlFor="power-of-attorney-checkbox"></label>
                <div>
                  <label className="toggle-label" htmlFor="power-of-attorney-checkbox">
                    Продавец действует по доверенности
                  </label>
                  <div className="toggle-description">
                    {sellerActsByPowerOfAttorney
                      ? 'Заполните данные доверителя и представителя'
                      : 'Включите, если продавец действует через представителя'
                    }
                  </div>
                </div>
              </div>

              {/* Поля доверенности - показываются только при выборе доверенности */}
              {sellerActsByPowerOfAttorney && (
                <div className="power-of-attorney-section">
                  <div className="info-message" style={{
                    backgroundColor: '#e7f3ff',
                    border: '1px solid #b3d9ff',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '20px',
                    fontSize: '14px',
                    color: '#0056b3'
                  }}>
                    ℹ️ <strong>Внимание:</strong> Данные продавца, указанные выше, теперь являются данными <strong>представителя</strong>.
                    Ниже укажите данные настоящего <strong>собственника</strong> (доверителя).
                  </div>
                  <h4>Данные доверенности</h4>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Номер доверенности *</label>
                      <input
                        {...register('sellerPowerOfAttorneyNumber', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                        })}
                        placeholder="123/456"
                      />
                      {errors.sellerPowerOfAttorneyNumber && <span className="error">{errors.sellerPowerOfAttorneyNumber.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Дата выдачи доверенности *</label>
                      <input
                        type="date"
                        {...register('sellerPowerOfAttorneyDate', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                        })}
                      />
                      {errors.sellerPowerOfAttorneyDate && <span className="error">{errors.sellerPowerOfAttorneyDate.message}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Кем выдана доверенность (нотариус) *</label>
                    <input
                      {...register('sellerPowerOfAttorneyIssuer', {
                        required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                      })}
                      placeholder="Нотариус г. Москвы Иванова И.И."
                    />
                    {errors.sellerPowerOfAttorneyIssuer && <span className="error">{errors.sellerPowerOfAttorneyIssuer.message}</span>}
                  </div>

                  <div className="form-group">
                    <label>Адрес нотариуса</label>
                    <input
                      {...register('sellerPowerOfAttorneyIssuerAddress')}
                      placeholder="г. Москва, ул. Ленина, д. 10, оф. 5"
                    />
                  </div>

                  <h4>Данные доверителя (собственника)</h4>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Фамилия доверителя *</label>
                      <input
                        {...register('sellerPrincipalLastName', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                        })}
                        placeholder="Иванов"
                      />
                      {errors.sellerPrincipalLastName && <span className="error">{errors.sellerPrincipalLastName.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Имя доверителя *</label>
                      <input
                        {...register('sellerPrincipalFirstName', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                        })}
                        placeholder="Иван"
                      />
                      {errors.sellerPrincipalFirstName && <span className="error">{errors.sellerPrincipalFirstName.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Отчество доверителя</label>
                      <input
                        {...register('sellerPrincipalMiddleName')}
                        placeholder="Иванович"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Пол доверителя *</label>
                      <select {...register('sellerPrincipalGender', {
                        required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                      })}>
                        <option value="">Выберите пол</option>
                        <option value="male">Мужской</option>
                        <option value="female">Женский</option>
                      </select>
                      {errors.sellerPrincipalGender && <span className="error">{errors.sellerPrincipalGender.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Дата рождения доверителя *</label>
                      <input
                        type="date"
                        {...register('sellerPrincipalBirthDate', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                        })}
                      />
                      {errors.sellerPrincipalBirthDate && <span className="error">{errors.sellerPrincipalBirthDate.message}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Серия паспорта доверителя *</label>
                      <input
                        {...register('sellerPrincipalPassportSeries', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false,
                          pattern: { value: /^\d{4}$/, message: 'Введите 4 цифры' }
                        })}
                        placeholder="1234"
                        maxLength={4}
                      />
                      {errors.sellerPrincipalPassportSeries && <span className="error">{errors.sellerPrincipalPassportSeries.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Номер паспорта доверителя *</label>
                      <input
                        {...register('sellerPrincipalPassportNumber', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false,
                          pattern: { value: /^\d{6}$/, message: 'Введите 6 цифр' }
                        })}
                        placeholder="567890"
                        maxLength={6}
                      />
                      {errors.sellerPrincipalPassportNumber && <span className="error">{errors.sellerPrincipalPassportNumber.message}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Дата выдачи паспорта доверителя *</label>
                      <input
                        type="date"
                        {...register('sellerPrincipalPassportIssueDate', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                        })}
                      />
                      {errors.sellerPrincipalPassportIssueDate && <span className="error">{errors.sellerPrincipalPassportIssueDate.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Код подразделения доверителя *</label>
                      <input
                        {...register('sellerPrincipalPassportCode', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false,
                          pattern: { value: /^\d{3}-\d{3}$/, message: 'Формат: 123-456' }
                        })}
                        placeholder="123-456"
                      />
                      {errors.sellerPrincipalPassportCode && <span className="error">{errors.sellerPrincipalPassportCode.message}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Кем выдан паспорт доверителя *</label>
                    <input
                      {...register('sellerPrincipalPassportIssuer', {
                        required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                      })}
                      placeholder="ОТДЕЛОМ УФМС РОССИИ ПО ..."
                    />
                    {errors.sellerPrincipalPassportIssuer && <span className="error">{errors.sellerPrincipalPassportIssuer.message}</span>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Адрес регистрации доверителя *</label>
                      <textarea
                        {...register('sellerPrincipalAddress', {
                          required: sellerActsByPowerOfAttorney ? 'Обязательное поле' : false
                        })}
                        placeholder="г. Москва, ул. Ленина, д. 1, кв. 1"
                      />
                      {errors.sellerPrincipalAddress && <span className="error">{errors.sellerPrincipalAddress.message}</span>}
                    </div>
                    <div className="form-group">
                      <label>Телефон доверителя</label>
                      <input
                        {...register('sellerPrincipalPhone')}
                        placeholder="+7 (999) 123-45-67"
                      />
                    </div>
                  </div>
                </div>
              )}

              <h4>{sellerActsByPowerOfAttorney ? 'Данные доверенного лица (представителя)' : 'Данные продавца'}</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Фамилия *</label>
                  <input
                    {...register('sellerLastName', { required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false })}
                    placeholder="Иванов"
                  />
                  {errors.sellerLastName && <span className="error">{errors.sellerLastName.message}</span>}
                </div>
                <div className="form-group">
                  <label>Имя *</label>
                  <input
                    {...register('sellerFirstName', { required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false })}
                    placeholder="Иван"
                  />
                  {errors.sellerFirstName && <span className="error">{errors.sellerFirstName.message}</span>}
                </div>
                <div className="form-group">
                  <label>Отчество</label>
                  <input
                    {...register('sellerMiddleName')}
                    placeholder="Иванович"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Пол *</label>
                  <select {...register('sellerGender', { required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false })}>
                    <option value="">Выберите пол</option>
                    <option value="male">Мужской</option>
                    <option value="female">Женский</option>
                  </select>
                  {errors.sellerGender && <span className="error">{errors.sellerGender.message}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Серия паспорта *</label>
                  <input
                    {...register('sellerPassportSeries', {
                      required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false,
                      pattern: { value: /^\d{4}$/, message: 'Введите 4 цифры' }
                    })}
                    placeholder="1234"
                    maxLength={4}
                  />
                  {errors.sellerPassportSeries && <span className="error">{errors.sellerPassportSeries.message}</span>}
                </div>
                <div className="form-group">
                  <label>Номер паспорта *</label>
                  <input
                    {...register('sellerPassportNumber', {
                      required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false,
                      pattern: { value: /^\d{6}$/, message: 'Введите 6 цифр' }
                    })}
                    placeholder="567890"
                    maxLength={6}
                  />
                  {errors.sellerPassportNumber && <span className="error">{errors.sellerPassportNumber.message}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Дата выдачи паспорта *</label>
                  <input
                    type="date"
                    {...register('sellerPassportIssueDate', { required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false })}
                  />
                  {errors.sellerPassportIssueDate && <span className="error">{errors.sellerPassportIssueDate.message}</span>}
                </div>
                <div className="form-group">
                  <label>Код подразделения *</label>
                  <input
                    {...register('sellerPassportCode', {
                      required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false,
                      pattern: { value: /^\d{3}-\d{3}$/, message: 'Формат: 123-456' }
                    })}
                    placeholder="123-456"
                  />
                  {errors.sellerPassportCode && <span className="error">{errors.sellerPassportCode.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Кем выдан паспорт *</label>
                <input
                  {...register('sellerPassportIssuer', { required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false })}
                  placeholder="ОТДЕЛОМ УФМС РОССИИ ПО ..."
                />
                {errors.sellerPassportIssuer && <span className="error">{errors.sellerPassportIssuer.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Дата рождения *</label>
                  <input
                    type="date"
                    {...register('sellerBirthDate', { required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false })}
                  />
                  {errors.sellerBirthDate && <span className="error">{errors.sellerBirthDate.message}</span>}
                </div>
                <div className="form-group">
                  <label>Телефон</label>
                  <input
                    {...register('sellerPhone')}
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Номер банковской карты (опционально)</label>
                <input
                  {...register('sellerCardNumber')}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                />
                <div className="field-description">
                  Для переводов на карту. Номер будет указан в реквизитах договора.
                </div>
              </div>

              <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#666' }}>Банковские реквизиты (опционально)</h5>
              <div className="field-description" style={{ marginBottom: '15px' }}>
                Для безналичных переводов на расчетный счет. Если не заполнено, в договоре будут только указаны реквизиты юрлица (если есть) или номер карты.
              </div>

              <div className="form-group">
                <label>Наименование банка</label>
                <input
                  {...register('sellerIndividualBankName')}
                  placeholder="ПАО СБЕРБАНК"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>БИК</label>
                  <input
                    {...register('sellerIndividualBik')}
                    placeholder="044525225"
                    maxLength={9}
                  />
                </div>
                <div className="form-group">
                  <label>Корреспондентский счет</label>
                  <input
                    {...register('sellerIndividualCorrespondentAccount')}
                    placeholder="30101810400000000225"
                    maxLength={20}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Расчетный счет</label>
                <input
                  {...register('sellerIndividualCheckingAccount')}
                  placeholder="40817810099910004312"
                  maxLength={20}
                />
              </div>

              <div className="form-group">
                <label>Адрес регистрации *</label>
                <textarea
                  {...register('sellerAddress', { required: watch('sellerType') === 'individual' ? 'Обязательное поле' : false })}
                  placeholder="г. Москва, ул. Ленина, д. 1, кв. 1"
                />
                {errors.sellerAddress && <span className="error">{errors.sellerAddress.message}</span>}
              </div>
            </>
          ) : (
            <>
              {/* Поля для Юридического лица */}
              <div className="form-group">
                <label>Полное наименование организации *</label>
                <input
                  {...register('sellerLegalName', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder='ООО "Ромашка"'
                />
                {errors.sellerLegalName && <span className="error">{errors.sellerLegalName.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ИНН *</label>
                  <input
                    {...register('sellerInn', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="7700000000"
                  />
                  {errors.sellerInn && <span className="error">{errors.sellerInn.message}</span>}
                </div>
                <div className="form-group">
                  <label>КПП</label>
                  <input
                    {...register('sellerKpp')}
                    placeholder="770101001"
                  />
                </div>
                <div className="form-group">
                  <label>ОГРН *</label>
                  <input
                    {...register('sellerOgrn', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="1027700000000"
                  />
                  {errors.sellerOgrn && <span className="error">{errors.sellerOgrn.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Юридический адрес *</label>
                <textarea
                  {...register('sellerLegalAddress', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder="123456, г. Москва, ул. Примерная, д. 1"
                />
                {errors.sellerLegalAddress && <span className="error">{errors.sellerLegalAddress.message}</span>}
              </div>

              <div className="form-group">
                <label>Почтовый адрес</label>
                <textarea
                  {...register('sellerMailingAddress')}
                  placeholder="Если отличается от юридического"
                />
              </div>

              <h4>Банковские реквизиты</h4>

              <div className="form-group">
                <label>Наименование банка *</label>
                <input
                  {...register('sellerBankName', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder='ПАО Сбербанк'
                />
                {errors.sellerBankName && <span className="error">{errors.sellerBankName.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>БИК *</label>
                  <input
                    {...register('sellerBik', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="044525225"
                  />
                  {errors.sellerBik && <span className="error">{errors.sellerBik.message}</span>}
                </div>
                <div className="form-group">
                  <label>Корр. счет *</label>
                  <input
                    {...register('sellerCorrespondentAccount', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="30101810400000000225"
                  />
                  {errors.sellerCorrespondentAccount && <span className="error">{errors.sellerCorrespondentAccount.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Расчетный счет *</label>
                <input
                  {...register('sellerCheckingAccount', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder="40702810000000000000"
                />
                {errors.sellerCheckingAccount && <span className="error">{errors.sellerCheckingAccount.message}</span>}
              </div>

              <h4>Подписант</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>ФИО руководителя/подписанта *</label>
                  <input
                    {...register('sellerDirectorName', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="Иванов Иван Иванович"
                  />
                  {errors.sellerDirectorName && <span className="error">{errors.sellerDirectorName.message}</span>}
                </div>
                <div className="form-group">
                  <label>Должность *</label>
                  <input
                    {...register('sellerDirectorPosition', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="Генеральный директор"
                  />
                  {errors.sellerDirectorPosition && <span className="error">{errors.sellerDirectorPosition.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Действует на основании *</label>
                <input
                  {...register('sellerActingBasis', { required: watch('sellerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder="Устава"
                />
                {errors.sellerActingBasis && <span className="error">{errors.sellerActingBasis.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Телефон</label>
                  <input
                    {...register('sellerLegalPhone')}
                    placeholder="+7 (495) 000-00-00"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    {...register('sellerLegalEmail')}
                    placeholder="info@company.com"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Данные покупателя */}
        <div className="form-section">
          <h3>Данные покупателя</h3>

          <div className="form-group">
            <label>Тип покупателя</label>
            <div className="contract-type-selector" style={{ marginBottom: '20px' }}>
              <button
                type="button"
                className={`contract-type-btn ${watch('buyerType') === 'individual' ? 'active' : ''}`}
                onClick={() => setValue('buyerType', 'individual')}
                style={{ flex: 1, padding: '10px' }}
              >
                <span className="contract-type-label">Физическое лицо</span>
              </button>
              <button
                type="button"
                className={`contract-type-btn ${watch('buyerType') === 'legal_entity' ? 'active' : ''}`}
                onClick={() => setValue('buyerType', 'legal_entity')}
                style={{ flex: 1, padding: '10px' }}
              >
                <span className="contract-type-label">Юридическое лицо</span>
              </button>
            </div>
            <input type="hidden" {...register('buyerType')} />
          </div>

          <div className="form-group">
            <label>Выбрать покупателя из существующих договоров</label>
            <PersonSelector
              type="buyer"
              persons={getUniqueBuyers()}
              selectedPerson={selectedBuyer}
              onPersonSelect={handleBuyerSelect}
              placeholder="Поиск покупателя..."
            />
          </div>

          {watch('buyerType') === 'individual' ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Фамилия *</label>
                  <input
                    {...register('buyerLastName', { required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false })}
                    placeholder="Петров"
                  />
                  {errors.buyerLastName && <span className="error">{errors.buyerLastName.message}</span>}
                </div>
                <div className="form-group">
                  <label>Имя *</label>
                  <input
                    {...register('buyerFirstName', { required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false })}
                    placeholder="Петр"
                  />
                  {errors.buyerFirstName && <span className="error">{errors.buyerFirstName.message}</span>}
                </div>
                <div className="form-group">
                  <label>Отчество</label>
                  <input
                    {...register('buyerMiddleName')}
                    placeholder="Петрович"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Пол *</label>
                  <select {...register('buyerGender', { required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false })}>
                    <option value="">Выберите пол</option>
                    <option value="male">Мужской</option>
                    <option value="female">Женский</option>
                  </select>
                  {errors.buyerGender && <span className="error">{errors.buyerGender.message}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Серия паспорта *</label>
                  <input
                    {...register('buyerPassportSeries', {
                      required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false,
                      pattern: { value: /^\d{4}$/, message: 'Введите 4 цифры' }
                    })}
                    placeholder="5678"
                    maxLength={4}
                  />
                  {errors.buyerPassportSeries && <span className="error">{errors.buyerPassportSeries.message}</span>}
                </div>
                <div className="form-group">
                  <label>Номер паспорта *</label>
                  <input
                    {...register('buyerPassportNumber', {
                      required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false,
                      pattern: { value: /^\d{6}$/, message: 'Введите 6 цифр' }
                    })}
                    placeholder="901234"
                    maxLength={6}
                  />
                  {errors.buyerPassportNumber && <span className="error">{errors.buyerPassportNumber.message}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Дата выдачи паспорта *</label>
                  <input
                    type="date"
                    {...register('buyerPassportIssueDate', { required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false })}
                  />
                  {errors.buyerPassportIssueDate && <span className="error">{errors.buyerPassportIssueDate.message}</span>}
                </div>
                <div className="form-group">
                  <label>Код подразделения *</label>
                  <input
                    {...register('buyerPassportCode', {
                      required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false,
                      pattern: { value: /^\d{3}-\d{3}$/, message: 'Формат: 123-456' }
                    })}
                    placeholder="654-321"
                  />
                  {errors.buyerPassportCode && <span className="error">{errors.buyerPassportCode.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Кем выдан паспорт *</label>
                <input
                  {...register('buyerPassportIssuer', { required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false })}
                  placeholder="ОТДЕЛОМ УФМС РОССИИ ПО ..."
                />
                {errors.buyerPassportIssuer && <span className="error">{errors.buyerPassportIssuer.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Дата рождения *</label>
                  <input
                    type="date"
                    {...register('buyerBirthDate', { required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false })}
                  />
                  {errors.buyerBirthDate && <span className="error">{errors.buyerBirthDate.message}</span>}
                </div>
                <div className="form-group">
                  <label>Телефон</label>
                  <input
                    {...register('buyerPhone')}
                    placeholder="+7 (999) 987-65-43"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Номер банковской карты (опционально)</label>
                <input
                  {...register('buyerCardNumber')}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                />
                <div className="field-description">
                  Для переводов на карту. Номер будет указан в реквизитах договора.
                </div>
              </div>

              <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#666' }}>Банковские реквизиты (опционально)</h5>
              <div className="field-description" style={{ marginBottom: '15px' }}>
                Для безналичных переводов на расчетный счет. Если не заполнено, в договоре будут только указаны реквизиты юрлица (если есть) или номер карты.
              </div>

              <div className="form-group">
                <label>Наименование банка</label>
                <input
                  {...register('buyerIndividualBankName')}
                  placeholder="ПАО СБЕРБАНК"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>БИК</label>
                  <input
                    {...register('buyerIndividualBik')}
                    placeholder="044525225"
                    maxLength={9}
                  />
                </div>
                <div className="form-group">
                  <label>Корреспондентский счет</label>
                  <input
                    {...register('buyerIndividualCorrespondentAccount')}
                    placeholder="30101810400000000225"
                    maxLength={20}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Расчетный счет</label>
                <input
                  {...register('buyerIndividualCheckingAccount')}
                  placeholder="40817810099910004312"
                  maxLength={20}
                />
              </div>

              <div className="form-group">
                <label>Адрес регистрации *</label>
                <textarea
                  {...register('buyerAddress', { required: watch('buyerType') === 'individual' ? 'Обязательное поле' : false })}
                  placeholder="г. Москва, ул. Пушкина, д. 2, кв. 2"
                />
                {errors.buyerAddress && <span className="error">{errors.buyerAddress.message}</span>}
              </div>
            </>
          ) : (
            <>
              {/* Поля для Юридического лица */}
              <div className="form-group">
                <label>Полное наименование организации *</label>
                <input
                  {...register('buyerLegalName', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder='ООО "Ромашка"'
                />
                {errors.buyerLegalName && <span className="error">{errors.buyerLegalName.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ИНН *</label>
                  <input
                    {...register('buyerInn', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="7700000000"
                  />
                  {errors.buyerInn && <span className="error">{errors.buyerInn.message}</span>}
                </div>
                <div className="form-group">
                  <label>КПП</label>
                  <input
                    {...register('buyerKpp')}
                    placeholder="770101001"
                  />
                </div>
                <div className="form-group">
                  <label>ОГРН *</label>
                  <input
                    {...register('buyerOgrn', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="1027700000000"
                  />
                  {errors.buyerOgrn && <span className="error">{errors.buyerOgrn.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Юридический адрес *</label>
                <textarea
                  {...register('buyerLegalAddress', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder="123456, г. Москва, ул. Примерная, д. 1"
                />
                {errors.buyerLegalAddress && <span className="error">{errors.buyerLegalAddress.message}</span>}
              </div>

              <div className="form-group">
                <label>Почтовый адрес</label>
                <textarea
                  {...register('buyerMailingAddress')}
                  placeholder="Если отличается от юридического"
                />
              </div>

              <h4>Банковские реквизиты</h4>

              <div className="form-group">
                <label>Наименование банка *</label>
                <input
                  {...register('buyerBankName', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder='ПАО Сбербанк'
                />
                {errors.buyerBankName && <span className="error">{errors.buyerBankName.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>БИК *</label>
                  <input
                    {...register('buyerBik', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="044525225"
                  />
                  {errors.buyerBik && <span className="error">{errors.buyerBik.message}</span>}
                </div>
                <div className="form-group">
                  <label>Корр. счет *</label>
                  <input
                    {...register('buyerCorrespondentAccount', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="30101810400000000225"
                  />
                  {errors.buyerCorrespondentAccount && <span className="error">{errors.buyerCorrespondentAccount.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Расчетный счет *</label>
                <input
                  {...register('buyerCheckingAccount', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder="40702810000000000000"
                />
                {errors.buyerCheckingAccount && <span className="error">{errors.buyerCheckingAccount.message}</span>}
              </div>

              <h4>Подписант</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>ФИО руководителя/подписанта *</label>
                  <input
                    {...register('buyerDirectorName', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="Иванов Иван Иванович"
                  />
                  {errors.buyerDirectorName && <span className="error">{errors.buyerDirectorName.message}</span>}
                </div>
                <div className="form-group">
                  <label>Должность *</label>
                  <input
                    {...register('buyerDirectorPosition', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                    placeholder="Генеральный директор"
                  />
                  {errors.buyerDirectorPosition && <span className="error">{errors.buyerDirectorPosition.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Действует на основании *</label>
                <input
                  {...register('buyerActingBasis', { required: watch('buyerType') === 'legal_entity' ? 'Обязательное поле' : false })}
                  placeholder="Устава"
                />
                {errors.buyerActingBasis && <span className="error">{errors.buyerActingBasis.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Телефон</label>
                  <input
                    {...register('buyerLegalPhone')}
                    placeholder="+7 (495) 000-00-00"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    {...register('buyerLegalEmail')}
                    placeholder="info@company.com"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Данные земельного участка */}
        <div className="form-section">
          <h3>Данные земельного участка</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Кадастровый номер *</label>
              <input
                {...register('cadastralNumber', { required: 'Обязательное поле' })}
                placeholder="12:34:5678901:123"
              />
              {errors.cadastralNumber && <span className="error">{errors.cadastralNumber.message}</span>}
            </div>
            <div className="form-group">
              <label>Площадь (кв.м) *</label>
              <input
                type="number"
                step="0.01"
                {...register('area', {
                  required: 'Обязательное поле',
                  min: { value: 0.01, message: 'Площадь должна быть больше 0' }
                })}
                placeholder="600"
              />
              {errors.area && <span className="error">{errors.area.message}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Категория земель *</label>
              <select {...register('category', { required: 'Обязательное поле' })}>
                <option value="">Выберите категорию</option>
                <option value="Земли населенных пунктов">Земли населенных пунктов</option>
                <option value="Земли сельскохозяйственного назначения">Земли сельскохозяйственного назначения</option>
                <option value="Земли промышленности">Земли промышленности</option>
                <option value="Земли лесного фонда">Земли лесного фонда</option>
              </select>
              {errors.category && <span className="error">{errors.category.message}</span>}
            </div>
            <div className="form-group">
              <label>Разрешенное использование *</label>
              <input
                {...register('purpose', { required: 'Обязательное поле' })}
                placeholder="Для индивидуального жилищного строительства"
              />
              {errors.purpose && <span className="error">{errors.purpose.message}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Адрес участка *</label>
            <textarea
              {...register('landAddress', { required: 'Обязательное поле' })}
              placeholder="Московская область, г. Подольск, ул. Дачная, участок 15"
            />
            {errors.landAddress && <span className="error">{errors.landAddress.message}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Вид права *</label>
              <select {...register('rightType', { required: 'Обязательное поле' })}>
                <option value="">Выберите вид права</option>
                <option value="Собственность">Собственность</option>
                <option value="Пожизненное наследуемое владение">Пожизненное наследуемое владение</option>
                <option value="Постоянное (бессрочное) пользование">Постоянное (бессрочное) пользование</option>
              </select>
              {errors.rightType && <span className="error">{errors.rightType.message}</span>}
            </div>
            <div className="form-group">
              <label>Дата регистрации права *</label>
              <input
                type="date"
                {...register('rightRegistrationDate', { required: 'Обязательное поле' })}
              />
              {errors.rightRegistrationDate && <span className="error">{errors.rightRegistrationDate.message}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Номер регистрации права *</label>
            <input
              {...register('rightRegistrationNumber', { required: 'Обязательное поле' })}
              placeholder="50-50-01/123/2023-456"
            />
            {errors.rightRegistrationNumber && <span className="error">{errors.rightRegistrationNumber.message}</span>}
          </div>

          <div className="form-group">
            <label>Основание государственной регистрации *</label>
            <input
              {...register('registrationBasis', { required: 'Обязательное поле' })}
              placeholder="Свидетельство о государственной регистрации права"
            />
            {errors.registrationBasis && <span className="error">{errors.registrationBasis.message}</span>}
          </div>
        </div>

        {/* Условия сделки */}
        <div className="form-section">
          <h3>Условия сделки</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Город сделки *</label>
              <input
                {...register('dealCity', { required: 'Обязательное поле' })}
                placeholder="Москва"
              />
              {errors.dealCity && <span className="error">{errors.dealCity.message}</span>}
            </div>
            <div className="form-group">
              <label>Дата сделки *</label>
              <input
                type="date"
                {...register('dealDate', { required: 'Обязательное поле' })}
              />
              {errors.dealDate && <span className="error">{errors.dealDate.message}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Сумма сделки (руб.) *</label>
              <input
                type="number"
                step="0.01"
                {...register('amount', {
                  required: 'Обязательное поле',
                  min: { value: 1, message: 'Сумма должна быть больше 0' }
                })}
                placeholder="1500000"
              />
              {errors.amount && <span className="error">{errors.amount.message}</span>}
            </div>
          </div>

          {/* Способ оплаты */}
          <div className="form-group">
            <label>Способ оплаты *</label>
            <select {...register('paymentMethod', { required: 'Выберите способ оплаты' })}>
              <option value="bank_transfer">Безналичный перевод (на расчетный счет)</option>
              <option value="cash">Наличные</option>
              <option value="card_transfer">Перевод на банковскую карту</option>
              <option value="mixed">Смешанная оплата</option>
            </select>
            {errors.paymentMethod && <span className="error">{errors.paymentMethod.message}</span>}
          </div>

          {/* Поля для смешанной оплаты */}
          {watch('paymentMethod') === 'mixed' && (
            <div className="form-row">
              <div className="form-group">
                <label>Сумма наличными (руб.) *</label>
                <input
                  type="number"
                  step="0.01"
                  {...register('cashAmount', {
                    required: watch('paymentMethod') === 'mixed' ? 'Обязательное поле' : false,
                    min: { value: 0, message: 'Сумма не может быть отрицательной' }
                  })}
                  placeholder="500000"
                />
                {errors.cashAmount && <span className="error">{errors.cashAmount.message}</span>}
              </div>
              <div className="form-group">
                <label>Сумма переводом (руб.) *</label>
                <input
                  type="number"
                  step="0.01"
                  {...register('transferAmount', {
                    required: watch('paymentMethod') === 'mixed' ? 'Обязательное поле' : false,
                    min: { value: 0, message: 'Сумма не может быть отрицательной' }
                  })}
                  placeholder="1000000"
                />
                {errors.transferAmount && <span className="error">{errors.transferAmount.message}</span>}
              </div>
            </div>
          )}

          {/* Поля для договора с рассрочкой */}
          {contractType === 'installment' && (
            <div className="installment-section">
              <h4>Условия рассрочки</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Первоначальный взнос (руб.)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('initialPayment')}
                    placeholder="0"
                  />
                  <div className="field-description">
                    Сумма, уплачиваемая при подписании договора
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      {...register('hasEncumbrance')}
                    />
                    Установить обременение (залог) до полной оплаты
                  </label>
                  <div className="field-description">
                    Обременение регистрируется в Росреестре и снимается после полной оплаты
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Штраф за просрочку платежа</label>
                <textarea
                  {...register('latePaymentPenalty')}
                  placeholder="Например: 0.1% от суммы просроченного платежа за каждый день просрочки"
                  rows={2}
                />
                <div className="field-description">
                  Укажите размер и порядок начисления штрафа за просрочку платежа
                </div>
              </div>

              <div className="form-group">
                <label>График платежей *</label>
                <div className="installment-payments-list">
                  {installmentPayments.length === 0 ? (
                    <div className="info-message" style={{
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '10px',
                      fontSize: '14px',
                      color: '#856404'
                    }}>
                      ⚠️ Добавьте хотя бы один платеж в график
                    </div>
                  ) : (
                    installmentPayments.map((payment, index) => (
                      <div key={index} className="installment-payment-item">
                        <div className="installment-payment-header">
                          <span className="payment-number">Платеж {index + 1}</span>
                          <button
                            type="button"
                            className="remove-payment-btn"
                            onClick={() => removeInstallmentPayment(index)}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Сумма платежа (руб.) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={payment.amount || ''}
                              onChange={(e) => updateInstallmentPayment(index, 'amount', e.target.value ? parseFloat(e.target.value) : '')}
                              placeholder="0"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Срок оплаты *</label>
                            <input
                              type="date"
                              value={payment.dueDate || ''}
                              onChange={(e) => updateInstallmentPayment(index, 'dueDate', e.target.value)}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Описание платежа (необязательно)</label>
                          <input
                            type="text"
                            value={payment.description || ''}
                            onChange={(e) => updateInstallmentPayment(index, 'description', e.target.value)}
                            placeholder="Например: Первый платеж, Финальный платеж"
                          />
                        </div>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    className="add-payment-btn"
                    onClick={addInstallmentPayment}
                  >
                    + Добавить платеж
                  </button>
                </div>
              </div>

              <div className="info-message" style={{
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '6px',
                padding: '12px',
                marginTop: '15px',
                fontSize: '14px',
                color: '#0c5460'
              }}>
                ℹ️ <strong>Важно:</strong> Договор с рассрочкой подается на регистрацию в Росреестр с указанием обременения (если установлено).
                После полной оплаты необходимо подать заявление о снятии обременения.
              </div>
            </div>
          )}

          {/* Поля для предварительного договора */}
          {contractType === 'preliminary' && (
            <div className="preliminary-section">
              <h4>Условия предварительного договора</h4>

              <div className="form-group">
                <label>Срок заключения основного договора *</label>
                <input
                  type="date"
                  {...register('mainContractDeadline', {
                    required: contractType === 'preliminary' ? 'Обязательное поле' : false
                  })}
                />
                <div className="field-description">
                  Дата, до которой должен быть заключен основной договор купли-продажи
                </div>
                {errors.mainContractDeadline && <span className="error">{errors.mainContractDeadline.message}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Задаток/аванс (руб.)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('deposit')}
                    placeholder="0"
                  />
                  <div className="field-description">
                    Сумма задатка или аванса, уплачиваемая при подписании предварительного договора
                  </div>
                </div>
                <div className="form-group">
                  <label>Тип платежа</label>
                  <select {...register('depositType')}>
                    <option value="">Не выбран</option>
                    <option value="deposit">Задаток</option>
                    <option value="advance">Аванс</option>
                  </select>
                  <div className="field-description">
                    Задаток обеспечивает обязательство, аванс - предоплата. Выберите тип, если указана сумма.
                  </div>
                </div>
              </div>

              <div className="info-message" style={{
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '6px',
                padding: '12px',
                marginTop: '15px',
                fontSize: '14px',
                color: '#0c5460'
              }}>
                ℹ️ <strong>Важно:</strong> Предварительный договор обязывает стороны заключить основной договор купли-продажи в указанный срок.
                Задаток обеспечивает обязательство и имеет правовые последствия при неисполнении. Аванс является предоплатой и подлежит возврату при неисполнении обязательства.
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Дополнительные условия</label>
            <textarea
              {...register('additionalConditions')}
              placeholder="Особые условия договора, если есть"
              rows={4}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={disabled}>
            {disabled ? 'Сохранение...' : (initialData ? 'Обновить договор' : 'Создать договор')}
          </button>
        </div>
      </form>
    </div>
  );
}; 